import { DEMO_COMPANION_BOOKING_MESSAGES } from '../data/companionsData';

export type SupportedLanguage = 'en' | 'bn' | 'hi';

export interface LanguageMetadata {
  code: SupportedLanguage;
  label: string;
  nativeLabel: string;
  shortLabel: string;
}

export const SUPPORTED_LANGUAGES: LanguageMetadata[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', shortLabel: 'EN' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা', shortLabel: 'বাং' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', shortLabel: 'हिं' },
];

export const TRANSLATIONS: Record<SupportedLanguage, Record<string, string>> = {
  en: {
    // Brand & Common
    'app.name': 'PairUp Today',
    'app.tagline': 'India’s Trusted Verified Social & Event Companionship Platform',
    'common.language': 'Language',
    'common.selectLanguage': 'Select Language',
    'common.continue': 'Continue',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.save': 'Save Changes',
    'common.saved': 'Saved',
    'common.loading': 'Loading...',
    'common.verified': 'Verified',
    'common.demoProfile': 'Showcase Profile',
    'common.demoBadge': 'Showcase',
    'demo.badge': 'Showcase',
    'common.perHour': '/hr',
    'common.search': 'Search',
    'common.back': 'Back',

    // Navigation
    'nav.home': 'Home',
    'nav.findCompanions': 'Find Companions',
    'nav.howItWorks': 'How It Works',
    'nav.safety': 'Safety',
    'nav.membership': 'Membership',
    'nav.becomeCompanion': 'Become a Companion',
    'nav.login': 'Login',
    'nav.register': 'Sign Up',
    'nav.loginRegister': 'Login / Register',
    'nav.dashboard': 'Dashboard',
    'nav.myBookings': 'My Bookings',
    'nav.wishlist': 'Wishlist',
    'nav.messages': 'Messages',
    'nav.earnings': 'Earnings & Payouts',
    'nav.kycVerification': 'KYC & Bank Verification',
    'nav.profile': 'My Profile',
    'nav.settings': 'Settings',
    'nav.logout': 'Log Out',
    'nav.switchToClient': 'Client Mode',
    'nav.switchToCompanion': 'Companion Mode',

    // Hero & Discovery
    'hero.badge': '100% ID-Verified Safe Public Companionship',
    'hero.title': 'Find Your Perfect Companion for Every Occasion',
    'hero.subtitle': 'Book verified companions for dining, travel, cultural walks, corporate events, and chauffeur-assisted tours across India.',
    'hero.exploreBtn': 'Explore Companions',
    'hero.becomeCompanionBtn': 'Join as Companion',
    'hero.findMale': 'Find Male Companion',
    'hero.findFemale': 'Find Female Companion',
    'discovery.title': 'Explore Companions',
    'discovery.subtitle': 'Browse verified companions and showcase profiles across Indian cities',
    'discovery.searchPlaceholder': 'Search by city, locality, or companion name...',
    'discovery.nearbyTitle': 'Companions Near You',
    'discovery.bookNow': 'Book Now',
    'discovery.viewProfile': 'View Profile',
    'discovery.demoCannotBook': 'Not available on this time / date',

    // Companion Booking Restriction (Exact Mandatory Messages)
    'demo.bookingRestrictedTitle': 'Not available on this time / date',
    'demo.bookingRestrictedMessage': 'Not available on this time / date',
    'demo.bannerNotice': 'Showcase Profile: You can view photos and details, but bookings are enabled only for verified real Companion accounts.',

    // Authentication
    'auth.clientLoginTitle': 'Client Sign In',
    'auth.companionLoginTitle': 'Companion Sign In',
    'auth.mobileOtpTab': 'Mobile + OTP',
    'auth.emailPasswordTab': 'Email & Password',
    'auth.sendOtp': 'Send OTP via SMS',
    'auth.verifyAndLogin': 'Verify OTP & Sign In',
    'auth.createAccount': 'Create New Account',
    'auth.existingAccountNotice': 'Only registered accounts can sign in. New users must complete registration first.',
    'auth.adminPortalTitle': 'PairUpToday Admin Login',
    'auth.admin2faRequired': 'Mandatory 2FA Verification Required',

    // First-Open Permissions
    'permissions.title': 'Permissions Required for Safe Experience',
    'permissions.subtitle': 'PairUp Today uses location for nearby companion discovery and camera for identity verification.',
    'permissions.locationTitle': 'Location Access (Nearby Companions)',
    'permissions.locationDesc': 'Find verified companions and safe public meeting venues in your city.',
    'permissions.cameraTitle': 'Camera & Microphone Access',
    'permissions.cameraDesc': 'Required for live KYC selfie verification and profile photo upload.',
    'permissions.notificationTitle': 'Booking & OTP Notifications',
    'permissions.notificationDesc': 'Receive instant booking updates, meetup check-in alerts, and security codes.',
    'permissions.allowContinue': 'Allow & Continue',

    // Booking Modal
    'booking.title': 'Confirm Companion Booking',
    'booking.selectDate': 'Meeting Date',
    'booking.selectTime': 'Start Time',
    'booking.duration': 'Duration',
    'booking.meetingLocation': 'Public Meeting Venue (India)',
    'booking.payAndConfirm': 'Pay & Confirm Booking via Razorpay',
    'booking.escrowNotice': '100% Escrow Protected: Companion payout is released only after in-person meetup OTP verification.',

    // Membership
    'membership.title': 'Companion Membership Plans',
    'membership.subtitle': 'Activate your verified Companion profile with server-verified Razorpay checkout.',
    'membership.starter': '1 Month Starter Plan (₹449)',
    'membership.professional': '3 Months Professional Plan (₹1,099)',
    'membership.elite': '1 Year Elite Plan (₹5,999)',

    // KYC & Bank Verification
    'kyc.title': 'Identity & Bank Verification',
    'kyc.subtitle': 'Complete Government ID KYC and Pennyless Bank Verification to receive payouts.',
    'kyc.bankTitle': 'Companion Bank Verification (APITXT Pennyless)',
    'kyc.verifyBankBtn': 'Verify Bank Account',

    // Settings
    'settings.languageTitle': 'Language Preference (ভাষা / भाषा)',
    'settings.languageDesc': 'Select your preferred language for Web, Android, and iOS. Changes apply immediately.',

    // CTA & Companion Onboarding (Requirement 7 & 9)
    'cta.becomeCompanion': 'BECOME A COMPANION',
    'cta.earnAmount': 'EARN UP TO ₹1 LAKH',
    'cta.supportingText': 'Join PairUpToday and start your journey.',
    'cta.applyNow': 'Apply Now',

    // Companion Registration Steps (Requirement 3 & 9)
    'companionReg.title': 'Become a Companion • Earn Up to ₹1 Lakh',
    'companionReg.subtitle': 'Join India’s most trusted companion platform. Set your own schedule, connect with verified clients, and earn up to ₹1,00,000 per month safely.',
    'companionReg.step1': 'Account',
    'companionReg.step2': 'Profile',
    'companionReg.step3': 'KYC Info',
    'companionReg.step4': 'Safety & Terms',
    'companionReg.legalName': 'Legal Name (As on Government ID)',
    'companionReg.idType': 'ID Document Type',
    'companionReg.idNumber': 'ID Number / Reference',
    'companionReg.frontSide': 'Document Photo Upload – Front Side',
    'companionReg.backSide': 'Document Photo Upload – Back Side',
    'companionReg.selfie': 'Selfie Upload',
    'companionReg.panExemptNote': 'Back side upload is not required for PAN Card.',
    'companionReg.submitBtn': 'Submit Registration',
    'companionReg.backBtn': 'Back',
    'companionReg.nextBtn': 'Next',

    // Companion Registration Wizard (Requirement 3, 9 & T-3)
    'companionReg.step1Title': 'Step 1: Basic Account Credentials',
    'companionReg.step2Title': 'Step 2: Companion Profile & Rates',
    'companionReg.step3Title': 'Step 3: Identity & KYC Documents',
    'companionReg.step4Title': 'Step 4: Safety, Security & Terms',
    'companionReg.freeSignup': '100% Free Signup',
    'companionReg.govName': 'Government / Legal Name *',
    'companionReg.displayName': 'Public Display Name *',
    'companionReg.email': 'Email Address *',
    'companionReg.phone': 'Mobile Phone (10-Digit Indian) *',
    'companionReg.sendOtp': 'Send OTP',
    'companionReg.verifyOtp': 'Verify OTP',
    'companionReg.gender': 'Gender Identity',
    'companionReg.dob': 'Date of Birth (Must be 18+)',
    'companionReg.city': 'Operating City (India)',
    'companionReg.hourlyRate': 'Hourly Companionship Rate (₹)',
    'companionReg.bio': 'About You / Host Bio',
    'companionReg.services': 'Offered Companionship Services',
    'companionReg.driverVerification': 'Driver Service Verification (Optional)',
    'companionReg.drivingLicence': 'Driving Licence Number',
    'companionReg.drivingLicenceExpiry': 'Licence Expiry Date',
    'companionReg.drivingLicenceFront': 'Driving Licence Front',
    'companionReg.drivingLicenceBack': 'Driving Licence Back',
    'companionReg.aadhaar': 'Aadhaar Card (Front & Back)',
    'companionReg.pan': 'PAN Card (Front Side Only)',
    'companionReg.voterId': 'Voter ID (Front & Back)',
    'companionReg.passport': 'Passport (Required Identity Pages)',
    'companionReg.acceptTerms': 'I accept the PairUpToday Terms of Service & Privacy Policy',
    'companionReg.acceptSafety': 'I commit to safe public-only meetups & safety guidelines',
    'companionReg.successTitle': 'Registration Received! Account Pending Review',
    'companionReg.successMessage': 'Your application and KYC documents have been securely stored in PostgreSQL and Firestore. Admin will review your identity.',
    'companionReg.enterDashboard': 'Enter Companion Dashboard',
    'companionReg.chooseMembership': 'Step 3: Choose Membership & Activate via Razorpay',

    // Membership Plans & Checkout (T-2 & T-3)
    'membership.badge': 'Verified Host Activation',
    'membership.pageTitle': 'Choose Your Companion Plan',
    'membership.pageSubtitle': 'Secure your verified profile with official Razorpay Checkout',
    'membership.starterPlan': 'Starter Plan',
    'membership.professionalPlan': 'Professional Plan',
    'membership.elitePlan': 'Elite Plan',
    'membership.payViaRazorpay': 'Proceed to Razorpay Payment',
    'membership.paymentSuccess': 'Payment Successful & Verified!',
    'membership.paymentPending': 'Payment Under Processing / Awaiting Webhook Confirmation',
    'membership.paymentFailed': 'Payment Failed or Verification Rejected',
    'membership.activeNotice': 'Your membership is active! Complete Admin KYC review to start receiving client bookings.',
    'membership.ruleNotice': 'Account Active = Verified Membership Payment + Admin KYC Approval',

    // KYC Verification (T-1 & T-3)
    'kyc.pageTitle': 'Identity Verification Desk',
    'kyc.pageSubtitle': 'Upload mandatory Government ID (Front & Back) and live face selfie',
    'kyc.documentType': 'Select Document Type',
    'kyc.frontSide': 'Front Side (Photo & ID Number)',
    'kyc.backSide': 'Back Side (Address & Details)',
    'kyc.selfieTitle': 'Live Face Selfie Verification',
    'kyc.uploadFront': 'Upload Front Side',
    'kyc.uploadBack': 'Upload Back Side',
    'kyc.takeSelfie': 'Take Selfie with Camera',
    'kyc.submitReview': 'Submit All Documents for Admin Review',
    'kyc.statusUnderReview': 'Under Review by Admin',
    'kyc.statusVerified': 'Verified & Approved',
    'kyc.statusRejected': 'Rejected — Re-upload Required',
    'kyc.bankAccount': 'Bank Account Number',
    'kyc.ifsc': 'IFSC Code',
    'kyc.accountHolder': 'Account Holder Name',
    'kyc.bankSubtitle': 'APITXT Pennyless Instant Verification',

    // Companion Dashboard (T-2 & T-3)
    'companionDashboard.title': 'Companion Host Dashboard',
    'companionDashboard.available': 'Available for Bookings',
    'companionDashboard.paused': 'Paused / Offline',
    'companionDashboard.activationBlocked': 'Activation Blocked: Requires both Verified Membership Payment and Admin KYC Approval.',
    'companionDashboard.earnings': 'Earnings & Wallet',
    'companionDashboard.bookings': 'Client Bookings',
    'companionDashboard.startOtp': 'Verify Meeting Start OTP',
    'companionDashboard.completeOtp': 'Meeting Completion OTP',
    'companionDashboard.tabAll': 'All Bookings',
    'companionDashboard.tabPending': 'Pending',
    'companionDashboard.tabConfirmed': 'Confirmed',
    'companionDashboard.tabCompleted': 'Completed',

    // Admin Review Panel (T-1 & T-3)
    'admin.overview': 'Overview',
    'admin.kycDesk': 'KYC Review Desk',
    'admin.companions': 'Companions',
    'admin.clients': 'Clients',
    'admin.payments': 'Payments',
    'admin.auditLogs': 'Audit Logs',
    'admin.approveDoc': 'Approve Document',
    'admin.rejectDoc': 'Reject Document',
    'admin.reuploadDoc': 'Request Re-upload',
    'admin.reasonLabel': 'Reason / Audit Note *',

    // Core Keys (Requirement 9)
    'companions.verifiedBadge': 'Verified Host',
    'nav.companions': 'Companions',
    'nav.bookings': 'Bookings',
    'nav.paymentHistory': 'Payment History',
    'companions.bookNow': 'Book Now',
    'nav.signup': 'Sign Up',
    'companions.title': 'Verified Social Companions',
    'companions.subtitle': 'Explore verified companion hosts across India for social, corporate, dining, and travel events.',
    'settings.title': 'Account Settings & Preferences',
    'settings.savedSuccess': 'Settings saved successfully!',
  },

  bn: {
    // Brand & Common
    'app.name': 'PairUp Today',
    'app.tagline': 'ভারতের বিশ্বস্ত ও ভেরিফাইড সোশ্যাল এবং ইভেন্ট কম্প্যানিয়নশিপ প্ল্যাটফর্ম',
    'common.language': 'ভাষা',
    'common.selectLanguage': 'ভাষা নির্বাচন করুন',
    'common.continue': 'এগিয়ে যান',
    'common.cancel': 'বাতিল করুন',
    'common.close': 'বন্ধ করুন',
    'common.save': 'পরিবর্তন সংরক্ষণ করুন',
    'common.saved': 'সংরক্ষিত হয়েছে',
    'common.loading': 'লোড হচ্ছে...',
    'common.verified': 'ভেরিফাইড',
    'common.demoProfile': 'প্রদর্শনী প্রোফাইল',
    'common.demoBadge': 'প্রদর্শনী',
    'demo.badge': 'প্রদর্শনী',
    'common.perHour': '/ঘণ্টা',
    'common.search': 'অনুসন্ধান',
    'common.back': 'ফিরে যান',

    // Navigation
    'nav.home': 'হোম',
    'nav.findCompanions': 'Companion খুঁজুন',
    'nav.howItWorks': 'কীভাবে কাজ করে',
    'nav.safety': 'নিরাপত্তা',
    'nav.membership': 'মেম্বারশিপ',
    'nav.becomeCompanion': 'Companion হিসেবে যোগ দিন',
    'nav.login': 'লগইন',
    'nav.register': 'রেজিস্টার',
    'nav.loginRegister': 'লগইন / রেজিস্টার',
    'nav.dashboard': 'ড্যাশবোর্ড',
    'nav.myBookings': 'আমার বুকিংসমূহ',
    'nav.wishlist': 'উইশলিস্ট',
    'nav.messages': 'মেসেজ',
    'nav.earnings': 'আয় ও পেআউট',
    'nav.kycVerification': 'KYC ও ব্যাংক ভেরিফিকেশন',
    'nav.profile': 'আমার প্রোফাইল',
    'nav.settings': 'সেটিংস',
    'nav.logout': 'লগ আউট',
    'nav.switchToClient': 'Client মোড',
    'nav.switchToCompanion': 'Companion মোড',

    // Hero & Discovery
    'hero.badge': '১০০% আইডি-ভেরিফাইড নিরাপদ পাবলিক কম্প্যানিয়নশিপ',
    'hero.title': 'প্রতিটি বিশেষ মুহূর্তের জন্য আপনার উপযুক্ত Companion খুঁজুন',
    'hero.subtitle': 'ডাইনিং, ভ্রমণ, কালচারাল ট্যুর, কর্পোরেট ইভেন্ট এবং ড্রাইভার-সহ ট্যুরের জন্য ভেরিফাইড Companion বুক করুন।',
    'hero.exploreBtn': 'Companion খুঁজুন',
    'hero.becomeCompanionBtn': 'Companion হিসেবে যুক্ত হোন',
    'hero.findMale': 'পুরুষ সঙ্গী খুঁজুন (Find Male Companion)',
    'hero.findFemale': 'নারী সঙ্গী খুঁজুন (Find Female Companion)',
    'discovery.title': 'Companion প্রোফাইলসমূহ দেখুন',
    'discovery.subtitle': 'ভারতের বিভিন্ন শহরে ভেরিফাইড Companion এবং প্রদর্শনী প্রোফাইল ব্রাউজ করুন',
    'discovery.searchPlaceholder': 'শহর, এলাকা বা নাম দিয়ে খুঁজুন...',
    'discovery.nearbyTitle': 'আপনার কাছাকাছি Companion',
    'discovery.bookNow': 'বুক করুন',
    'discovery.viewProfile': 'প্রোফাইল দেখুন',
    'discovery.demoCannotBook': 'Not available on this time / date',

    // Companion Booking Restriction (Exact Mandatory Messages)
    'demo.bookingRestrictedTitle': 'Not available on this time / date',
    'demo.bookingRestrictedMessage': 'Not available on this time / date',
    'demo.bannerNotice': 'প্রদর্শনী প্রোফাইল: আপনি ছবি ও তথ্য দেখতে পারবেন, কিন্তু শুধুমাত্র ভেরিফাইড আসল Companion প্রোফাইল বুক করা যাবে।',

    // Authentication
    'auth.clientLoginTitle': 'Client লগইন',
    'auth.companionLoginTitle': 'Companion লগইন',
    'auth.mobileOtpTab': 'মোবাইল + OTP',
    'auth.emailPasswordTab': 'ইমেইল ও পাসওয়ার্ড',
    'auth.sendOtp': 'SMS-এ OTP পাঠান',
    'auth.verifyAndLogin': 'OTP যাচাই করে লগইন করুন',
    'auth.createAccount': 'নতুন অ্যাকাউন্ট খুলুন',
    'auth.existingAccountNotice': 'শুধুমাত্র রেজিস্টার্ড অ্যাকাউন্ট লগইন করতে পারবে। নতুন ব্যবহারকারীদের আগে অ্যাকাউন্ট রেজিস্টার করতে হবে।',
    'auth.adminPortalTitle': 'PairUpToday অ্যাডমিন লগইন',
    'auth.admin2faRequired': 'বাধ্যতামূলক 2FA যাচাইকরণ প্রয়োজন',

    // First-Open Permissions
    'permissions.title': 'নিরাপদ ব্যবহারের জন্য অনুমতি প্রয়োজন',
    'permissions.subtitle': 'কাছাকাছি Companion খোঁজার জন্য লোকেশন এবং KYC যাচাইয়ের জন্য ক্যামেরা অনুমতি প্রয়োজন।',
    'permissions.locationTitle': 'লোকেশন অ্যাক্সেস (কাছাকাছি Companion)',
    'permissions.locationDesc': 'আপনার শহরে ভেরিফাইড Companion এবং নিরাপদ পাবলিক মিটিং স্থান খুঁজতে সাহায্য করে।',
    'permissions.cameraTitle': 'ক্যামেরা ও মাইক্রোফোন অ্যাক্সেস',
    'permissions.cameraDesc': 'লাইভ KYC সেলফি ভেরিফিকেশন এবং প্রোফাইল ছবি আপলোডের জন্য প্রয়োজন।',
    'permissions.notificationTitle': 'বুকিং ও OTP নোটিফিকেশন',
    'permissions.notificationDesc': 'বুকিং আপডেট, মিটআপ চেক-ইন এবং সিকিউরিটি OTP সাথে সাথে পান।',
    'permissions.allowContinue': 'অনুমতি দিন ও এগিয়ে যান',

    // Booking Modal
    'booking.title': 'Companion বুকিং নিশ্চিত করুন',
    'booking.selectDate': 'সাক্ষাতের তারিখ',
    'booking.selectTime': 'শুরুর সময়',
    'booking.duration': 'সময়কাল',
    'booking.meetingLocation': 'পাবলিক মিটিং স্থান (ভারত)',
    'booking.payAndConfirm': 'Razorpay দিয়ে পেমেন্ট ও বুকিং নিশ্চিত করুন',
    'booking.escrowNotice': '১০০% এসক্রো সুরক্ষিত: সরাসরি সাক্ষাতের পর OTP যাচাই হলে তবেই Companion পেআউট রিলিজ হয়।',

    // Membership
    'membership.title': 'Companion মেম্বারশিপ প্ল্যান',
    'membership.subtitle': 'সার্ভার-ভেরিফাইড Razorpay পেমেন্টের মাধ্যমে আপনার Companion প্রোফাইল সক্রিয় করুন।',
    'membership.starter': '১ মাসের স্টার্টার প্ল্যান (₹449)',
    'membership.professional': '৩ মাসের প্রফেশনাল প্ল্যান (₹1,099)',
    'membership.elite': '১ বছরের এলিট প্ল্যান (₹5,999)',

    // KYC & Bank Verification
    'kyc.title': 'পরিচয়পত্র (KYC) ও ব্যাংক ভেরিফিকেশন',
    'kyc.subtitle': 'পেআউট পেতে সরকারি আইডি KYC এবং পেনিলেস ব্যাংক ভেরিফিকেশন সম্পন্ন করুন।',
    'kyc.bankTitle': 'Companion ব্যাংক ভেরিফিকেশন (APITXT Pennyless)',
    'kyc.verifyBankBtn': 'ব্যাংক অ্যাকাউন্ট যাচাই করুন',

    // Settings
    'settings.languageTitle': 'ভাষা পছন্দ (Language Preference)',
    'settings.languageDesc': 'Web, Android এবং iOS-এর জন্য আপনার পছন্দের ভাষা নির্বাচন করুন। পরিবর্তন সাথে সাথে কার্যকর হবে।',

    // CTA & Companion Onboarding (Requirement 7 & 9)
    'cta.becomeCompanion': 'কম্প্যানিয়ন হিসেবে যোগ দিন',
    'cta.earnAmount': 'প্রতি মাসে ₹১ লাখ পর্যন্ত আয় করুন',
    'cta.supportingText': 'PairUpToday-তে যোগ দিন এবং আপনার যাত্রা শুরু করুন।',
    'cta.applyNow': 'আবেদন করুন',

    // Companion Registration Steps (Requirement 3 & 9)
    'companionReg.title': 'কম্প্যানিয়ন হোন • ₹১ লাখ পর্যন্ত আয় করুন',
    'companionReg.subtitle': 'ভারতের সবচেয়ে বিশ্বস্ত কম্প্যানিয়ন প্ল্যাটফর্মে যোগ দিন। নিজের সময়সূচী ঠিক করুন এবং নিরাপদে আয় করুন।',
    'companionReg.step1': 'অ্যাকাউন্ট',
    'companionReg.step2': 'প্রোফাইল',
    'companionReg.step3': 'কেওয়াইসি তথ্য',
    'companionReg.step4': 'নিরাপত্তা ও শর্তাবলী',
    'companionReg.legalName': 'আইনি নাম (সরকারি আইডি অনুযায়ী)',
    'companionReg.idType': 'আইডি নথির প্রকার',
    'companionReg.idNumber': 'আইডি নম্বর / রেফারেন্স',
    'companionReg.frontSide': 'নথির সামনের ছবি আপলোড',
    'companionReg.backSide': 'নথির পেছনের ছবি আপলোড',
    'companionReg.selfie': 'সেলফি আপলোড',
    'companionReg.panExemptNote': 'প্যান কার্ডের ক্ষেত্রে পেছনের ছবি প্রয়োজন নেই।',
    'companionReg.submitBtn': 'নিবন্ধন জমা দিন',
    'companionReg.backBtn': 'পেছনে',
    'companionReg.nextBtn': 'পরবর্তী',

    // Companion Registration Wizard (Requirement 3, 9 & T-3)
    'companionReg.step1Title': 'ধাপ ১: প্রাথমিক অ্যাকাউন্ট তথ্য',
    'companionReg.step2Title': 'ধাপ ২: কম্প্যানিয়ন প্রোফাইল ও রেট',
    'companionReg.step3Title': 'ধাপ ৩: পরিচয় ও কেওয়াইসি নথি',
    'companionReg.step4Title': 'ধাপ ৪: নিরাপত্তা ও শর্তাবলী',
    'companionReg.freeSignup': '১০০% ফ্রি সাইন আপ',
    'companionReg.govName': 'সরকারি / আইনি নাম *',
    'companionReg.displayName': 'পাবলিক ডিসপ্লে নাম *',
    'companionReg.email': 'ইমেল ঠিকানা *',
    'companionReg.phone': 'মোবাইল নম্বর (১০-সংখ্যার ভারতীয়) *',
    'companionReg.sendOtp': 'ওটিপি পাঠান',
    'companionReg.verifyOtp': 'ওটিপি যাচাই করুন',
    'companionReg.gender': 'লিঙ্গ পরিচয়',
    'companionReg.dob': 'জন্ম তারিখ (১৮+ হতে হবে)',
    'companionReg.city': 'কার্যক্রমের শহর (ভারত)',
    'companionReg.hourlyRate': 'প্রতি ঘণ্টার রেট (₹)',
    'companionReg.bio': 'আপনার সম্পর্কে বিবরণ',
    'companionReg.services': 'প্রদত্ত সেবা সমূহ',
    'companionReg.driverVerification': 'ড্রাইভার সেবা যাচাইকরণ (ঐচ্ছিক)',
    'companionReg.drivingLicence': 'ড্রাইভিং লাইসেন্স নম্বর',
    'companionReg.drivingLicenceExpiry': 'লাইসেন্স মেয়াদ শেষ',
    'companionReg.drivingLicenceFront': 'ড্রাইভিং লাইসেন্স সামনের পাশ',
    'companionReg.drivingLicenceBack': 'ড্রাইভিং লাইসেন্স পেছনের পাশ',
    'companionReg.aadhaar': 'আধার কার্ড (সামনে ও পেছনে)',
    'companionReg.pan': 'প্যান কার্ড (শুধুমাত্র সামনে)',
    'companionReg.voterId': 'ভোটার আইডি (সামনে ও পেছনে)',
    'companionReg.passport': 'পাসপোর্ট (প্রয়োজনীয় পৃষ্ঠাসমূহ)',
    'companionReg.acceptTerms': 'আমি সেবার শর্তাবলী ও গোপনীয়তা নীতি গ্রহণ করছি',
    'companionReg.acceptSafety': 'আমি নিরাপদ পাবলিক মিটআপ নীতি মানতে অঙ্গীকারবদ্ধ',
    'companionReg.successTitle': 'নিবন্ধন গৃহীত হয়েছে! অ্যাকাউন্ট পর্যালোচনার অধীনে',
    'companionReg.successMessage': 'আপনার আবেদন ও কেওয়াইসি নথি সফলভাবে সংরক্ষিত হয়েছে। অ্যাডমিন আপনার পরিচয় পর্যালোচনা করবেন।',
    'companionReg.enterDashboard': 'কম্প্যানিয়ন ড্যাশবোর্ডে প্রবেশ করুন',
    'companionReg.chooseMembership': 'ধাপ ৩: মেম্বারশিপ নির্বাচন ও সক্রিয় করুন',

    // Membership Plans & Checkout (T-2 & T-3)
    'membership.badge': 'ভেরিফাইড হোস্ট সক্রিয়করণ',
    'membership.pageTitle': 'আপনার কম্প্যানিয়ন প্ল্যান বেছে নিন',
    'membership.pageSubtitle': 'অফিসিয়াল Razorpay চেকআউট দিয়ে আপনার ভেরিফাইড প্রোফাইল সক্রিয় করুন',
    'membership.starterPlan': 'স্টার্টার প্ল্যান',
    'membership.professionalPlan': 'প্রফেশনাল প্ল্যান',
    'membership.elitePlan': 'এলিট প্ল্যান',
    'membership.payViaRazorpay': 'Razorpay পেমেন্টে এগিয়ে যান',
    'membership.paymentSuccess': 'পেমেন্ট সফল ও যাচাইকৃত!',
    'membership.paymentPending': 'পেমেন্ট প্রক্রিয়াধীন / নিশ্চিতকরণের অপেক্ষায়',
    'membership.paymentFailed': 'পেমেন্ট ব্যর্থ বা যাচাইকরণ বাতিল হয়েছে',
    'membership.activeNotice': 'আপনার মেম্বারশিপ সক্রিয়! ক্লায়েন্ট বুকিং পেতে অ্যাডমিন কেওয়াইসি অনুমোদন সম্পন্ন করুন।',
    'membership.ruleNotice': 'অ্যাকাউন্ট সক্রিয় = যাচাইকৃত পেমেন্ট + অ্যাডমিন কেওয়াইসি অনুমোদন',

    // KYC Verification (T-1 & T-3)
    'kyc.pageTitle': 'পরিচয় যাচাইকরণ ডেস্ক',
    'kyc.pageSubtitle': 'বাধ্যতামূলক সরকারি আইডি (সামনে ও পেছনে) এবং লাইভ সেলফি আপলোড করুন',
    'kyc.documentType': 'নথির ধরন নির্বাচন করুন',
    'kyc.frontSide': 'সামনের পাশ (ছবি ও আইডি নম্বর)',
    'kyc.backSide': 'পেছনের পাশ (ঠিকানা ও বিবরণ)',
    'kyc.selfieTitle': 'লাইভ সেলফি যাচাইকরণ',
    'kyc.uploadFront': 'সামনের পাশ আপলোড করুন',
    'kyc.uploadBack': 'পেছনের পাশ আপলোড করুন',
    'kyc.takeSelfie': 'ক্যামেরা দিয়ে সেলফি তুলুন',
    'kyc.submitReview': 'পর্যালোচনার জন্য সমস্ত নথি জমা দিন',
    'kyc.statusUnderReview': 'অ্যাডমিন পর্যালোচনার অধীনে',
    'kyc.statusVerified': 'যাচাইকৃত ও অনুমোদিত',
    'kyc.statusRejected': 'বাতিল — পুনরায় আপলোড আবশ্যক',
    'kyc.bankAccount': 'ব্যাংক অ্যাকাউন্ট নম্বর',
    'kyc.ifsc': 'আইএফএসসি কোড',
    'kyc.accountHolder': 'অ্যাকাউন্টধারীর নাম',
    'kyc.bankSubtitle': 'APITXT পেনীলেস তাৎক্ষণিক যাচাইকরণ',

    // Companion Dashboard (T-2 & T-3)
    'companionDashboard.title': 'কম্প্যানিয়ন হোস্ট ড্যাশবোর্ড',
    'companionDashboard.available': 'বুকিংয়ের জন্য উপলব্ধ',
    'companionDashboard.paused': 'বিরতি / অফলাইন',
    'companionDashboard.activationBlocked': 'সক্রিয়করণ অবরুদ্ধ: যাচাইকৃত পেমেন্ট এবং অ্যাডমিন কেওয়াইসি অনুমোদন উভয়ই আবশ্যক।',
    'companionDashboard.earnings': 'উপার্জন ও ওয়ালেট',
    'companionDashboard.bookings': 'ক্লায়েন্ট বুকিং',
    'companionDashboard.startOtp': 'সাক্ষাৎ শুরুর ওটিপি যাচাই করুন',
    'companionDashboard.completeOtp': 'সাক্ষাৎ সমাপ্তির ওটিপি',
    'companionDashboard.tabAll': 'সমস্ত বুকিং',
    'companionDashboard.tabPending': 'অপেক্ষারত',
    'companionDashboard.tabConfirmed': 'নিশ্চিত',
    'companionDashboard.tabCompleted': 'সম্পন্ন',

    // Admin Review Panel (T-1 & T-3)
    'admin.overview': 'সারসংক্ষেপ',
    'admin.kycDesk': 'কেওয়াইসি রিভিউ ডেস্ক',
    'admin.companions': 'কম্প্যানিয়নগণ',
    'admin.clients': 'ক্লায়েন্টগণ',
    'admin.payments': 'পেমেন্টসমূহ',
    'admin.auditLogs': 'অডিট লগ',
    'admin.approveDoc': 'নথি অনুমোদন করুন',
    'admin.rejectDoc': 'নথি বাতিল করুন',
    'admin.reuploadDoc': 'পুনরায় আপলোডের অনুরোধ করুন',
    'admin.reasonLabel': 'কারণ / অডিট নোট *',

    // Core Keys (Requirement 9)
    'companions.verifiedBadge': 'যাচাইকৃত হোস্ট',
    'nav.companions': 'সঙ্গীগণ',
    'nav.bookings': 'বুকিং',
    'nav.paymentHistory': 'পেমেন্ট ইতিহাস',
    'companions.bookNow': 'এখনই বুক করুন',
    'nav.signup': 'নিবন্ধন করুন',
    'companions.title': 'যাচাইকৃত সামাজিক সঙ্গী',
    'companions.subtitle': 'সামাজিক, করপোরেট, ডাইনিং এবং ভ্রমণ অনুষ্ঠানের জন্য ভারত জুড়ে যাচাইকৃত সঙ্গী খুঁজুন।',
    'settings.title': 'অ্যাকাউন্ট সেটিংস ও পছন্দসমূহ',
    'settings.savedSuccess': 'সেটিংস সফলভাবে সংরক্ষিত হয়েছে!',
  },

  hi: {
    // Brand & Common
    'app.name': 'PairUp Today',
    'app.tagline': 'भारत का विश्वसनीय और सत्यापित सोशल एवं इवेंट कम्पैनियनशिप प्लेटफ़ॉर्म',
    'common.language': 'भाषा',
    'common.selectLanguage': 'भाषा चुनें',
    'common.continue': 'आगे बढ़ें',
    'common.cancel': 'रद्द करें',
    'common.close': 'बंद करें',
    'common.save': 'परिवर्तन सहेजें',
    'common.saved': 'सहेजा गया',
    'common.loading': 'लोड हो रहा है...',
    'common.verified': 'सत्यापित',
    'common.demoProfile': 'प्रदर्शनी प्रोफ़ाइल',
    'common.demoBadge': 'प्रदर्शनी',
    'demo.badge': 'प्रदर्शनी',
    'common.perHour': '/घंटा',
    'common.search': 'खोजें',
    'common.back': 'वापस',

    // Navigation
    'nav.home': 'होम',
    'nav.findCompanions': 'Companion खोजें',
    'nav.howItWorks': 'यह कैसे काम करता है',
    'nav.safety': 'सुरक्षा',
    'nav.membership': 'सदस्यता (Membership)',
    'nav.becomeCompanion': 'Companion बनें',
    'nav.login': 'लॉगिन',
    'nav.register': 'साइन अप',
    'nav.loginRegister': 'लॉगिन / साइन अप',
    'nav.dashboard': 'डैशबोर्ड',
    'nav.myBookings': 'मेरी बुकिंग',
    'nav.wishlist': 'विशलिस्ट',
    'nav.messages': 'संदेश',
    'nav.earnings': 'कमाई और भुगतान',
    'nav.kycVerification': 'KYC और बैंक सत्यापन',
    'nav.profile': 'मेरी प्रोफ़ाइल',
    'nav.settings': 'सेटिंग्स',
    'nav.logout': 'लॉग आउट',
    'nav.switchToClient': 'Client मोड',
    'nav.switchToCompanion': 'Companion मोड',

    // Hero & Discovery
    'hero.badge': '100% आईडी-सत्यापित सुरक्षित पब्लिक कम्पैनियनशिप',
    'hero.title': 'हर अवसर के लिए अपना उपयुक्त Companion खोजें',
    'hero.subtitle': 'डाइनिंग, यात्रा, सांस्कृतिक भ्रमण, कॉर्पोरेट इवेंट और ड्राइवर-सहायता प्राप्त टूर के लिए सत्यापित Companion बुक करें।',
    'hero.exploreBtn': 'Companion खोजें',
    'hero.becomeCompanionBtn': 'Companion के रूप में जुड़ें',
    'hero.findMale': 'पुरुष साथी खोजें (Find Male Companion)',
    'hero.findFemale': 'महिला साथी खोजें (Find Female Companion)',
    'discovery.title': 'Companion प्रोफ़ाइल देखें',
    'discovery.subtitle': 'भारतीय शहरों में सत्यापित Companion और शोकेस प्रोफ़ाइल ब्राउज़ करें',
    'discovery.searchPlaceholder': 'शहर, क्षेत्र या Companion के नाम से खोजें...',
    'discovery.nearbyTitle': 'आपके आस-पास के Companion',
    'discovery.bookNow': 'अभी बुक करें',
    'discovery.viewProfile': 'प्रोफ़ाइल देखें',
    'discovery.demoCannotBook': 'Not available on this time / date',

    // Companion Booking Restriction (Exact Mandatory Messages)
    'demo.bookingRestrictedTitle': 'Not available on this time / date',
    'demo.bookingRestrictedMessage': 'Not available on this time / date',
    'demo.bannerNotice': 'प्रदर्शनी प्रोफ़ाइल: आप फ़ोटो और विवरण देख सकते हैं, लेकिन केवल सत्यापित वास्तविक Companion प्रोफ़ाइल ही बुक की जा सकती हैं।',

    // Authentication
    'auth.clientLoginTitle': 'Client लॉगिन',
    'auth.companionLoginTitle': 'Companion लॉगिन',
    'auth.mobileOtpTab': 'मोबाइल + OTP',
    'auth.emailPasswordTab': 'ईमेल और पासवर्ड',
    'auth.sendOtp': 'SMS द्वारा OTP भेजें',
    'auth.verifyAndLogin': 'OTP सत्यापित करें और लॉगिन करें',
    'auth.createAccount': 'नया खाता बनाएँ',
    'auth.existingAccountNotice': 'केवल पंजीकृत खाते ही लॉगिन कर सकते हैं। नए उपयोगकर्ताओं को पहले पंजीकरण पूरा करना होगा।',
    'auth.adminPortalTitle': 'PairUpToday एडमिन लॉगिन',
    'auth.admin2faRequired': 'अनिवार्य 2FA सत्यापन आवश्यक है',

    // First-Open Permissions
    'permissions.title': 'सुरक्षित अनुभव के लिए अनुमतियाँ आवश्यक हैं',
    'permissions.subtitle': 'आस-पास के Companion खोजने के लिए लोकेशन और पहचान सत्यापन के लिए कैमरा अनुमति आवश्यक है।',
    'permissions.locationTitle': 'लोकेशन एक्सेस (आस-पास के Companion)',
    'permissions.locationDesc': 'अपने शहर में सत्यापित Companion और सुरक्षित सार्वजनिक मीटिंग स्थान खोजें।',
    'permissions.cameraTitle': 'कैमरा और माइक्रोफ़ोन एक्सेस',
    'permissions.cameraDesc': 'लाइव KYC सेल्फ़ी सत्यापन और प्रोफ़ाइल फ़ोटो अपलोड के लिए आवश्यक।',
    'permissions.notificationTitle': 'बुकिंग और OTP सूचनाएँ',
    'permissions.notificationDesc': 'तुरंत बुकिंग अपडेट, मीटअप चेक-इन अलर्ट और सुरक्षा कोड प्राप्त करें।',
    'permissions.allowContinue': 'अनुमति दें और आगे बढ़ें',

    // Booking Modal
    'booking.title': 'Companion बुकिंग की पुष्टि करें',
    'booking.selectDate': 'मुलाकात की तारीख',
    'booking.selectTime': 'शुरू होने का समय',
    'booking.duration': 'अवधि',
    'booking.meetingLocation': 'सार्वजनिक मीटिंग स्थान (भारत)',
    'booking.payAndConfirm': 'Razorpay से भुगतान और बुकिंग की पुष्टि करें',
    'booking.escrowNotice': '100% एस्क्रो सुरक्षित: व्यक्तिगत मुलाकात के बाद OTP सत्यापन होने पर ही Companion भुगतान जारी किया जाता है।',

    // Membership
    'membership.title': 'Companion सदस्यता प्लान',
    'membership.subtitle': 'सर्वर-सत्यापित Razorpay भुगतान के साथ अपनी Companion प्रोफ़ाइल सक्रिय करें।',
    'membership.starter': '1 माह स्टार्टर प्लान (₹449)',
    'membership.professional': '3 माह प्रोफ़ेशनल प्लान (₹1,099)',
    'membership.elite': '1 वर्ष एलीट प्लान (₹5,999)',

    // KYC & Bank Verification
    'kyc.title': 'पहचान (KYC) और बैंक सत्यापन',
    'kyc.subtitle': 'भुगतान प्राप्त करने के लिए सरकारी आईडी KYC और पेनीलेस बैंक सत्यापन पूरा करें।',
    'kyc.bankTitle': 'Companion बैंक सत्यापन (APITXT Pennyless)',
    'kyc.verifyBankBtn': 'बैंक खाता सत्यापित करें',

    // Settings
    'settings.languageTitle': 'भाषा प्राथमिकता (Language Preference)',
    'settings.languageDesc': 'Web, Android और iOS के लिए अपनी पसंदीदा भाषा चुनें। बदलाव तुरंत लागू होंगे।',

    // CTA & Companion Onboarding (Requirement 7 & 9)
    'cta.becomeCompanion': 'कंपैनियन बनें',
    'cta.earnAmount': 'प्रति माह ₹1 लाख तक कमाएं',
    'cta.supportingText': 'PairUpToday से जुड़ें और अपनी यात्रा शुरू करें।',
    'cta.applyNow': 'आवेदन करें',

    // Companion Registration Steps (Requirement 3 & 9)
    'companionReg.title': 'कंपैनियन बनें • ₹1 लाख तक कमाएं',
    'companionReg.subtitle': 'भारत के सबसे भरोसेमंद कंपैनियन प्लेटफ़ॉर्म से जुड़ें। अपना समय तय करें और सुरक्षित रूप से कमाएं।',
    'companionReg.step1': 'अकाउंट',
    'companionReg.step2': 'प्रोफाइल',
    'companionReg.step3': 'केवाईसी विवरण',
    'companionReg.step4': 'सुरक्षा व शर्तें',
    'companionReg.legalName': 'कानूनी नाम (सरकारी आईडी के अनुसार)',
    'companionReg.idType': 'आईडी दस्तावेज प्रकार',
    'companionReg.idNumber': 'आईडी नंबर / संदर्भ',
    'companionReg.frontSide': 'दस्तावेज की सामने की फोटो अपलोड',
    'companionReg.backSide': 'दस्तावेज की पीछे की फोटो अपलोड',
    'companionReg.selfie': 'सेल्फी अपलोड',
    'companionReg.panExemptNote': 'पैन कार्ड के लिए पीछे की फोटो आवश्यक नहीं है।',
    'companionReg.submitBtn': 'पंजीकरण जमा करें',
    'companionReg.backBtn': 'पीछे',
    'companionReg.nextBtn': 'आगे बढ़ें',

    // Companion Registration Wizard (Requirement 3, 9 & T-3)
    'companionReg.step1Title': 'चरण 1: बुनियादी खाता क्रेडेंशियल्स',
    'companionReg.step2Title': 'चरण 2: कम्पैनियन प्रोफ़ाइल और दरें',
    'companionReg.step3Title': 'चरण 3: पहचान और केवाईसी दस्तावेज़',
    'companionReg.step4Title': 'चरण 4: सुरक्षा और सेवा शर्तें',
    'companionReg.freeSignup': '100% मुफ़्त साइन अप',
    'companionReg.govName': 'सरकारी / कानूनी नाम *',
    'companionReg.displayName': 'सार्वजनिक प्रदर्शन नाम *',
    'companionReg.email': 'ईमेल पता *',
    'companionReg.phone': 'मोबाइल नंबर (10-अंकीय भारतीय) *',
    'companionReg.sendOtp': 'ओटीपी भेजें',
    'companionReg.verifyOtp': 'ओटीपी सत्यापित करें',
    'companionReg.gender': 'लिंग पहचान',
    'companionReg.dob': 'जन्म तिथि (18+ होना अनिवार्य)',
    'companionReg.city': 'कार्यकारी शहर (भारत)',
    'companionReg.hourlyRate': 'प्रति घंटा दर (₹)',
    'companionReg.bio': 'अपने बारे में विवरण',
    'companionReg.services': 'प्रदान की जाने वाली सेवाएँ',
    'companionReg.driverVerification': 'ड्राइवर सेवा सत्यापन (वैकल्पिक)',
    'companionReg.drivingLicence': 'ड्राइविंग लाइसेंस नंबर',
    'companionReg.drivingLicenceExpiry': 'लाइसेंस समाप्ति तिथि',
    'companionReg.drivingLicenceFront': 'ड्राइविंग लाइसेंस सामने का भाग',
    'companionReg.drivingLicenceBack': 'ड्राइविंग लाइसेंस पीछे का भाग',
    'companionReg.aadhaar': 'आधार कार्ड (सामने और पीछे)',
    'companionReg.pan': 'पैन कार्ड (केवल सामने का भाग)',
    'companionReg.voterId': 'वोटर आईडी (सामने और पीछे)',
    'companionReg.passport': 'पासपोर्ट (आवश्यक पृष्ठ)',
    'companionReg.acceptTerms': 'मैं सेवा की शर्तें और गोपनीयता नीति स्वीकार करता/करती हूँ',
    'companionReg.acceptSafety': 'मैं सुरक्षित सार्वजनिक बैठकों के नियमों का पालन करने की प्रतिज्ञा करता/करती हूँ',
    'companionReg.successTitle': 'पंजीकरण प्राप्त हुआ! खाता समीक्षाधीन है',
    'companionReg.successMessage': 'आपका आवेदन और केवाईसी दस्तावेज़ सुरक्षित रूप से सहेजे गए हैं। व्यवस्थापक आपकी पहचान की समीक्षा करेंगे।',
    'companionReg.enterDashboard': 'कम्पैनियन डैशबोर्ड में प्रवेश करें',
    'companionReg.chooseMembership': 'चरण 3: सदस्यता चुनें और सक्रिय करें',

    // Membership Plans & Checkout (T-2 & T-3)
    'membership.badge': 'सत्यापित होस्ट सक्रियण',
    'membership.pageTitle': 'अपना कम्पैनियन प्लान चुनें',
    'membership.pageSubtitle': 'आधिकारिक Razorpay चेकआउट के साथ अपनी सत्यापित प्रोफ़ाइल सक्रिय करें',
    'membership.starterPlan': 'स्टार्टर प्लान',
    'membership.professionalPlan': 'प्रोफ़ेशनल प्लान',
    'membership.elitePlan': 'एलीट प्लान',
    'membership.payViaRazorpay': 'Razorpay भुगतान के लिए आगे बढ़ें',
    'membership.paymentSuccess': 'भुगतान सफल और सत्यापित!',
    'membership.paymentPending': 'भुगतान प्रसंस्करण में है / पुष्टिकरण की प्रतीक्षा है',
    'membership.paymentFailed': 'भुगतान विफल या सत्यापन अस्वीकृत',
    'membership.activeNotice': 'आपकी सदस्यता सक्रिय है! क्लाइंट बुकिंग प्राप्त करने के लिए व्यवस्थापक केवाईसी समीक्षा पूरी करें।',
    'membership.ruleNotice': 'खाता सक्रिय = सत्यापित सदस्यता भुगतान + व्यवस्थापक केवाईसी अनुमोदन',

    // KYC Verification (T-1 & T-3)
    'kyc.pageTitle': 'पहचान सत्यापन डेस्क',
    'kyc.pageSubtitle': 'अनिवार्य सरकारी आईडी (सामने और पीछे) और लाइव सेल्फी अपलोड करें',
    'kyc.documentType': 'दस्तावेज़ प्रकार चुनें',
    'kyc.frontSide': 'सामने का भाग (फोटो और आईडी नंबर)',
    'kyc.backSide': 'पीछे का भाग (पता और विवरण)',
    'kyc.selfieTitle': 'लाइव सेल्फी सत्यापन',
    'kyc.uploadFront': 'सामने का भाग अपलोड करें',
    'kyc.uploadBack': 'पीछे का भाग अपलोड करें',
    'kyc.takeSelfie': 'कैमरे से सेल्फी लें',
    'kyc.submitReview': 'व्यवस्थापक समीक्षा के लिए सभी दस्तावेज़ जमा करें',
    'kyc.statusUnderReview': 'व्यवस्थापक समीक्षाधीन',
    'kyc.statusVerified': 'सत्यापित और स्वीकृत',
    'kyc.statusRejected': 'अस्वीकृत — पुनः अपलोड आवश्यक',
    'kyc.bankAccount': 'बैंक खाता संख्या',
    'kyc.ifsc': 'IFSC कोड',
    'kyc.accountHolder': 'खाता धारक का नाम',
    'kyc.bankSubtitle': 'APITXT पेनीलेस त्वरित सत्यापन',

    // Companion Dashboard (T-2 & T-3)
    'companionDashboard.title': 'कम्पैनियन होस्ट डैशबोर्ड',
    'companionDashboard.available': 'बुकिंग के लिए उपलब्ध',
    'companionDashboard.paused': 'रोक दिया गया / ऑफ़लाइन',
    'companionDashboard.activationBlocked': 'सक्रियण अवरुद्ध: सत्यापित सदस्यता भुगतान और व्यवस्थापक केवाईसी अनुमोदन दोनों आवश्यक हैं।',
    'companionDashboard.earnings': 'कमाई और वॉलेट',
    'companionDashboard.bookings': 'क्लाइंट बुकिंग',
    'companionDashboard.startOtp': 'मीटिंग शुरू करने का OTP सत्यापित करें',
    'companionDashboard.completeOtp': 'मीटिंग पूर्णता OTP',
    'companionDashboard.tabAll': 'सभी बुकिंग',
    'companionDashboard.tabPending': 'लंबित',
    'companionDashboard.tabConfirmed': 'पुष्ट',
    'companionDashboard.tabCompleted': 'पूर्ण',

    // Admin Review Panel (T-1 & T-3)
    'admin.overview': 'अवलोकन',
    'admin.kycDesk': 'केवाईसी समीक्षा डेस्क',
    'admin.companions': 'कम्पैनियंस',
    'admin.clients': 'क्लाइंट्स',
    'admin.payments': 'भुगतान',
    'admin.auditLogs': 'ऑडिट लॉग',
    'admin.approveDoc': 'दस्तावेज़ स्वीकृत करें',
    'admin.rejectDoc': 'दस्तावेज़ अस्वीकृत करें',
    'admin.reuploadDoc': 'पुनः अपलोड का अनुरोध करें',
    'admin.reasonLabel': 'कारण / ऑडिट नोट *',

    // Core Keys (Requirement 9)
    'companions.verifiedBadge': 'सत्यापित होस्ट',
    'nav.companions': 'साथी (Companions)',
    'nav.bookings': 'बुकिंग',
    'nav.paymentHistory': 'भुगतान इतिहास',
    'companions.bookNow': 'अभी बुक करें',
    'nav.signup': 'साइन अप करें',
    'companions.title': 'सत्यापित सोशल साथी',
    'companions.subtitle': 'सामाजिक, कॉर्पोरेट, डाइनिंग और यात्रा कार्यक्रमों के लिए पूरे भारत में सत्यापित साथी खोजें।',
    'settings.title': 'खाता सेटिंग्स और प्राथमिकताएँ',
    'settings.savedSuccess': 'सेटिंग्स सफलतापूर्वक सहेजी गईं!',
  },
};

export function translateKey(lang: SupportedLanguage, key: string, fallback?: string): string {
  const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
  return dict[key] || TRANSLATIONS.en[key] || fallback || key;
}
